/**
 * sale-probability — «Вероятность продажи»: полоса вероятности, уверенность
 * модели и ключевые факторы. Зеркалит SaleProbabilitySection.tsx: тот же
 * гейт (`summary.saleProbability` не задана → пусто), тот же дисклеймер
 * («оценка не сверена с исходом сделки», всегда печатается вместе с баром),
 * то же поле `meta.confidenceLevel`, выведенное словом на языке отчёта через
 * общий с экраном `confidenceLevelLabel` (#1393), а без уровня строка не
 * печатается, как и на экране — это ДРУГОЕ представление уверенности, чем
 * проценты 94/70/40 в quality-score (там — `confidenceLevelPercent`, для той
 * же метрики).
 *
 * Индикаторы печатаются через `tag()` с вариантом по `impact`, а не
 * зелёными/красными стрелками экрана — на печатной странице нет ховера и
 * анимаций, плашка с цветом несёт тот же смысл компактнее.
 */
import { card, bar, kvRow, tag } from '../primitives';
import { scoreHex } from '../tokens';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';
import { confidenceLevelLabel } from '@/components/details/v2/analysis/analysis-utils';

const IMPACT_VARIANT = { positive: 'ok', negative: 'bad', neutral: 'muted' } as const;

export const renderSaleProbability: SectionFn = (input, t) => {
  const { summary, meta } = input.analysis.result;
  const raw = summary?.saleProbability;
  if (raw == null) return '';

  const pct = Math.round(raw * 100);
  const indicators = summary?.saleProbabilityIndicators;

  const confidenceLabel = confidenceLevelLabel(meta?.confidenceLevel, t);
  const confidenceHtml =
    confidenceLabel === null
      ? ''
      : kvRow(t('records.analysisPanel.confidenceLabel'), escapeHtml(confidenceLabel));

  const disclaimerHtml = `<div class="blk"><p style="color:var(--fg3);font-size:10.5px">${escapeHtml(
    t('records.analysisPanel.saleProbabilityDisclaimer')
  )}</p></div>`;

  const indicatorsHtml =
    indicators && indicators.length > 0
      ? `<div class="blk"><div class="lbl">${escapeHtml(t('records.analysisPanel.keyFactors'))}</div>` +
        indicators
          .map((ind) => {
            const confidenceChip =
              typeof ind.confidence === 'number'
                ? ` <span style="font-family:var(--mono);font-size:10px;color:var(--fg3)">${Math.round(ind.confidence * 100)}%</span>`
                : '';
            return `<div style="margin-bottom:4px">${tag(ind.indicator, IMPACT_VARIANT[ind.impact])}${confidenceChip}</div>`;
          })
          .join('') +
        '</div>'
      : '';

  return card({
    title: t('records.analysisPanel.saleProbability'),
    value: `<span style="font-family:var(--mono);font-weight:600;color:${scoreHex(pct)}">${pct}%</span>`,
    body: bar(pct) + confidenceHtml + disclaimerHtml + indicatorsHtml,
  });
};
